import FieldUtils from "utils/FieldUtils";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Mention, MentionsInput } from "react-mentions";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import AppMentionsInput from "./AppMentionsInput";

const InvalidFieldTypes = [
  "MultiSubitem",
  "Subitem",
  "Image",
  "SingleFile",
  "Audio",
  "Video",
  "Tags",
  "Object",
];

/**
 * DisplayName - Handles setup for a Module's Record's Name. Supports Module Fields dynamically as well as any static text.
 * Any module fields included in the Record Name are dynamically filled in when records are created/updated
 */
const DisplayName = ({
  fields,
  existingValue,
  onChange,
  readOnly = false,
  size,
  isSelfReference,
}) => {
  const [value, setValue] = useState("");
  const [options, setOptions] = useState([]);

  // Calculate the valid fields that can be included in a Record Name.
  const validFields = useMemo(() => {
    const theFields = fields ? fields : [];
    const validFields = [];

    for (const field of theFields) {
      if (isSelfReference && FieldUtils.isReference(field)) continue;
      if (field?.Sensitive === true) continue;
      if (InvalidFieldTypes.includes(field?.FieldType)) continue;

      if (
        (field?.SystemField && field?.APIName === "id") ||
        (field?.SystemField && field?.APIName === "_RecordName")
      ) {
        continue;
      }

      validFields.push(field.APIName);
    }

    return validFields;
  }, [fields]);

  const updateOptions = useCallback(() => {
    const theFields = fields ? fields : [];
    let out = [];

    for (let i = 0; i < theFields.length; i++) {
      if (!validFields.includes(theFields[i].APIName)) continue;

      out.push({ id: theFields[i].APIName, display: theFields[i].Name });
    }

    setOptions(out);
  }, [fields, validFields]);

  const updateValue = useCallback(() => {
    setValue(existingValue || "");

    updateOptions();
  }, [existingValue, updateOptions]);

  const handleInputChange = (e, newValue, newPlainTextValue, mentions) => {
    const variables = newValue.matchAll(/\{\{(.+?)\}\}/g);

    for (const variable of variables) {
      // Clear any invalid variables (In case the user manual enters an invalid field as a variable)
      if (!validFields.includes(variable[1])) {
        newValue = newValue.replace(variable[0], "");
      }
    }

    const mentionFieldsAPIMap = new Map();
    const mentionFieldsNameMap = new Map();
    mentions.forEach((value) => {
      mentionFieldsAPIMap.set(value.id, true);
      mentionFieldsNameMap.set(value.display, true);
    });

    // Retrieve unique values from the map
    const mentionFieldAPINames = Array.from(mentionFieldsAPIMap.keys());

    setValue(newValue);
    callFunctionIfExists(onChange, {
      newValue,
      mentionFieldAPINames,
      newPlainTextValue,
    });
  };

  useEffect(() => {
    if (existingValue) {
      updateValue();
    }

    updateOptions();
  }, [existingValue, updateValue, updateOptions]);

  useEffect(() => {
    updateValue();
  }, [existingValue, updateValue]);

  return (
    <>
      <AppMentionsInput
        readOnly={readOnly}
        size={size}
        value={value}
        handleInputChange={handleInputChange}
        options={options}
      />
    </>
  );
};

export default DisplayName;
